import type { AdminSport, IntegrationLeague, IntegrationMarket, IntegrationMarketInput, IntegrationStatus } from './api'
import { useMemo, useState } from 'react'
import { integrationKeyFromLabel, updateIntegrationLeague } from './api'
import { STATUS_META, STATUS_ORDER } from './IntegrationStatus'

// Edit form for ONE (integration, league) block — the market list with
// status / review flag / note per row, plus (on the Integrations tab) the
// league name and its catalog sport link. Saves through the full-replace
// RPC (migration 0070): the whole list goes up, an empty list removes the
// league from that integration. Used by both IntegrationsPane (tab) and
// IntegrationMarketsPanel (sport page, where the sport is fixed).

interface Props {
  integrationId: number
  integrationName: string
  // null league_key = a league this integration does not list yet; the key
  // is derived (see keyFor) and locked once saved.
  initial: {
    league_key: string | null
    league_name: string
    sport_id: number | null
    markets: IntegrationMarket[]
  }
  // Catalog for the sport picker. Omit (sport page) to lock name + sport.
  sports?: AdminSport[] | null
  // Leagues already listed under this integration — a new league whose
  // derived key collides is refused (edit the existing block instead).
  takenKeys: string[]
  // Keys other integrations already use for the same catalog sport, so a
  // newly added league reuses the sheet's shorthand (woo, hlrs, jai_alai…)
  // instead of minting a second key for the same sport.
  knownLeagues: Pick<IntegrationLeague, 'league_key' | 'league_name' | 'sport_id'>[]
  onSaved: () => void
  onCancel: () => void
}

interface Row extends Omit<IntegrationMarketInput, 'review_note'> {
  review_note: string
}

function toRows(markets: IntegrationMarket[]): Row[] {
  return markets.map(m => ({
    market_key: m.market_key,
    market_label: m.market_label,
    status: m.status,
    needs_review: m.needs_review,
    review_note: m.review_note ?? '',
  }))
}

export default function IntegrationLeagueEditor({
  integrationId,
  integrationName,
  initial,
  sports,
  takenKeys,
  knownLeagues,
  onSaved,
  onCancel,
}: Props) {
  const isNew = initial.league_key == null
  const lockMeta = sports === undefined
  const [leagueName, setLeagueName] = useState(initial.league_name)
  const [sportId, setSportId] = useState<number | null>(initial.sport_id)
  const [rows, setRows] = useState<Row[]>(() => toRows(initial.markets))
  const [draft, setDraft] = useState('')
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState<string | null>(null)

  // Key resolution for a new league: reuse whatever key the sheet already
  // uses for the chosen sport under another integration; else derive from
  // the slug (formula-drift → formula_drift); else from the typed name.
  const leagueKey = useMemo(() => {
    if (initial.league_key) return initial.league_key
    if (sportId != null) {
      const known = knownLeagues.find(l => l.sport_id === sportId)
      if (known) return known.league_key
      const sport = sports?.find(s => s.id === sportId)
      if (sport?.slug) return integrationKeyFromLabel(sport.slug)
    }
    return integrationKeyFromLabel(leagueName)
  }, [initial.league_key, sportId, knownLeagues, sports, leagueName])

  const keyTaken = isNew && leagueKey !== '' && takenKeys.includes(leagueKey)

  function pickSport(value: string) {
    const id = value === '' ? null : Number(value)
    setSportId(id)
    // Prefill the display name from the catalog when the name is still blank
    // or was itself a catalog name (so switching sports updates it).
    const sport = sports?.find(s => s.id === id)
    const wasCatalogName = leagueName.trim() === '' || sports?.some(s => s.name === leagueName.trim())
    if (sport && wasCatalogName) setLeagueName(sport.name)
  }

  function updateRow(i: number, patch: Partial<Row>) {
    setRows(prev => prev.map((r, idx) => idx === i ? { ...r, ...patch } : r))
  }

  function removeRow(i: number) {
    setRows(prev => prev.filter((_, idx) => idx !== i))
  }

  function addRow() {
    const label = draft.trim()
    if (!label) return
    const key = integrationKeyFromLabel(label)
    if (!key) {
      setErr(`"${label}" has no letters or digits to make a key from`)
      return
    }
    if (rows.some(r => r.market_key === key)) {
      setErr(`"${label}" is already listed (key ${key})`)
      return
    }
    setErr(null)
    setRows(prev => [...prev, { market_key: key, market_label: label, status: 'test', needs_review: false, review_note: '' }])
    setDraft('')
  }

  async function save(markets: Row[]) {
    setBusy(true)
    setErr(null)
    try {
      await updateIntegrationLeague(integrationId, leagueKey, {
        league_name: leagueName.trim(),
        sport_id: sportId,
        markets: markets.map(r => ({
          market_key: r.market_key,
          market_label: r.market_label.trim(),
          status: r.status,
          needs_review: r.needs_review,
          review_note: r.review_note.trim() === '' ? null : r.review_note.trim(),
        })),
      })
      onSaved()
    }
    catch (e) {
      setErr((e as Error).message)
      setBusy(false)
    }
  }

  function removeLeague() {
    // eslint-disable-next-line no-alert
    if (!window.confirm(`Remove ${leagueName.trim() || leagueKey} from ${integrationName}? All ${rows.length} market row${rows.length === 1 ? '' : 's'} go with it.`))
      return
    void save([])
  }

  const canSave = !busy
    && leagueName.trim() !== ''
    && leagueKey !== ''
    && !keyTaken
    && rows.every(r => r.market_label.trim() !== '')
    && (rows.length > 0 || !isNew)

  return (
    <div className="ad-int-editor">
      <div className="ad-int-editor-head">
        <span className="b-mono">
          {isNew ? 'ADD LEAGUE' : 'EDIT LEAGUE'}
          <span className="b-mono-dim ad-list-slug">
            {' '}
            ·
            {' '}
            {integrationName}
            {' '}
            ·
            {' '}
            {leagueKey || '—'}
          </span>
        </span>
        <div className="ad-markets-traded-actions">
          {!isNew && (
            <button type="button" className="b-btn-ghost b-btn-tiny" onClick={removeLeague} disabled={busy}>
              REMOVE LEAGUE
            </button>
          )}
          <button type="button" className="b-btn-ghost b-btn-tiny" onClick={onCancel} disabled={busy}>
            CANCEL
          </button>
          <button type="button" className="b-btn b-btn-primary b-btn-tiny" onClick={() => save(rows)} disabled={!canSave}>
            {busy ? 'SAVING…' : 'SAVE'}
          </button>
        </div>
      </div>

      {err && <p className="ad-msg b-mono is-err">{err}</p>}
      {keyTaken && (
        <p className="ad-msg b-mono is-err">
          {integrationName}
          {' '}
          already lists
          {' '}
          {leagueKey}
          {' '}
          — edit that block instead.
        </p>
      )}

      {!lockMeta && (
        <div className="ad-int-edit-meta">
          <label className="ad-field">
            <span className="b-mono-dim b-tiny">LEAGUE NAME</span>
            <input
              type="text"
              value={leagueName}
              onChange={e => setLeagueName(e.target.value)}
              className="ad-input"
              placeholder="e.g. Nitrocross (NRX)"
              disabled={busy}
            />
          </label>
          <label className="ad-field">
            <span className="b-mono-dim b-tiny">CATALOG SPORT — drives the sport page</span>
            <select
              value={sportId ?? ''}
              onChange={e => pickSport(e.target.value)}
              className="ad-int-select"
              disabled={busy || !sports}
            >
              <option value="">— not in catalog —</option>
              {(sports ?? [])
                .slice()
                .sort((a, b) => a.name.localeCompare(b.name))
                .map(s => (
                  <option key={s.id} value={s.id}>
                    {s.name}
                    {s.slug ? ` · ${s.slug}` : ''}
                  </option>
                ))}
            </select>
          </label>
        </div>
      )}

      <div className="ad-int-edit-rows">
        <div className="ad-int-edit-row is-head b-mono-dim b-tiny">
          <span>MARKET</span>
          <span>STATUS</span>
          <span>REVIEW</span>
          <span>NOTE</span>
          <span />
        </div>
        {rows.length === 0 && (
          <p className="b-mono-dim ad-list-empty">
            {isNew ? 'add at least one market' : 'no markets — SAVE removes this league from the integration'}
          </p>
        )}
        {rows.map((r, i) => (
          <div key={r.market_key} className="ad-int-edit-row">
            <input
              type="text"
              value={r.market_label}
              onChange={e => updateRow(i, { market_label: e.target.value })}
              className="ad-input"
              title={`key: ${r.market_key}`}
              disabled={busy}
            />
            <select
              value={r.status}
              onChange={e => updateRow(i, { status: e.target.value as IntegrationStatus })}
              className={`ad-int-select ${STATUS_META[r.status].cls}`}
              disabled={busy}
            >
              {STATUS_ORDER.map(s => (
                <option key={s} value={s}>{STATUS_META[s].label}</option>
              ))}
            </select>
            <label className="ad-int-edit-check" title="Needs review">
              <input
                type="checkbox"
                checked={r.needs_review}
                onChange={e => updateRow(i, { needs_review: e.target.checked })}
                className="ad-checkbox"
                disabled={busy}
              />
              <span aria-hidden="true">⚠</span>
            </label>
            <input
              type="text"
              value={r.review_note}
              onChange={e => updateRow(i, { review_note: e.target.value })}
              className="ad-input"
              placeholder="note…"
              disabled={busy}
            />
            <button
              type="button"
              onClick={() => removeRow(i)}
              className="ad-list-remove"
              aria-label={`Remove ${r.market_label}`}
              disabled={busy}
            >
              ✕
            </button>
          </div>
        ))}
      </div>

      <div className="ad-int-edit-add">
        <input
          type="text"
          value={draft}
          onChange={e => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault()
              addRow()
            }
          }}
          placeholder="Add a market… (e.g. Podium/Top3)"
          className="ad-input"
          disabled={busy}
        />
        <button type="button" onClick={addRow} disabled={busy || !draft.trim()} className="b-btn b-btn-tiny">
          ADD
        </button>
      </div>
    </div>
  )
}
